import { FormDialog } from '@/components/FormDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateBuilding, useUpdateBuilding } from '../hooks/useBuilding'
import { Building } from '../types/building'

export function BuildingDialog({
  open,
  onOpenChange,
  building,
  points,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  building: Building | null | undefined
  points?: number[][] | null
}) {
  const { t } = useTranslation()
  const createBuilding = useCreateBuilding()
  const updateBuilding = useUpdateBuilding()

  const [form, setForm] = useState({ name: '', totalFloor: 0 })

  const isEdit = !!building
  const isInvalid = !form.name
  const isLoading = createBuilding.isPending || updateBuilding.isPending

  useEffect(() => {
    if (building) {
      setForm({
        name: building.name,
        totalFloor: building.totalFloor,
      })
    } else {
      setForm({ name: '', totalFloor: 0 })
    }
  }, [building, open])

  function handleSubmit() {
    if (!form.name) return
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        showSuccess(isEdit ? t('floorplan.building.toast.editSuccess') : t('floorplan.building.toast.addSuccess'))
      },
      onError: (error: unknown) => {
        if (isApiError(error)) showError(error.message)
      },
    }

    const payload = {
      ...form,
      shape: points ? JSON.stringify(points) : null,
    }

    if (isEdit && building) {
      updateBuilding.mutate({ id: building.id, payload }, options)
    } else {
      createBuilding.mutate(payload, options)
    }
  }
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('floorplan.building.edit') : t('floorplan.building.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
    >
      <div className="flex flex-col gap-4 pt-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('floorplan.building.name')} *</Label>
          <Input
            data-testid="building-dialog-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="A"
          />
        </div>
      </div>
    </FormDialog>
  )
}
